import * as React from 'react'
import { Icon, IconType } from '@amzn/dvui/dist/Icon'
import { Tooltip } from '@amzn/dvui/dist/Tooltip'
import { spacerSpacing, spacerAlignItems, Spacer } from '@amzn/dvui/dist/Spacer'
import * as cn from 'classnames'
import { ResponsiveImage } from '../../../widgets/DVWebNodeDetail/components/images/responsiveImage'
import * as st from './titleListItem.module.scss'
import { SrcSize } from '../../../widgets/DVWebNodeDetail/utils/imageUtils'
import { Rating } from '../../../widgets/DVWebNodeDetail/components/badges/rating'
import { DecodedText } from '../../../widgets/DVWebNodeDetail/components/decodedText/decodedText'
import { Synopsis } from '../../../widgets/DVWebNodeDetail/components/synopsis/synopsis'
import { XRay } from '../../../widgets/DVWebNodeDetail/components/badges/xRay'
import { Hdr } from '../../../widgets/DVWebNodeDetail/components/badges/hdr'
import { Uhd } from '../../../widgets/DVWebNodeDetail/components/badges/uhd'
import { DetailFragment } from '../../../widgets/DVWebNodeDetail/reducers/detail'
import { safeBoolean } from '../../../widgets/DVWebNodeDetail/utils/utils'

const PACKSHOT_WIDTH_DESKTOP = 268
const PACKSHOT_WIDTH_MOBILE = 148

type Props = {
  uniqueId: string
  listIndex: number
  keepOpenOnSmallScreen?: boolean
  titleComp: React.ReactNode
  packshotProps: IPackshot
  metaDataComp?: React.ReactNode
  synopsis?: string
  additionalContent?: React.ReactNode
  showDetailListButton?: boolean
  detailListButtonProps?: React.HTMLAttributes<any>
}

interface IPackshot {
  packshotUrl?: string
  isWatched: boolean
  useResponsiveImage?: boolean
}

const buildTooltip = (title: string, content: string, id: string) => {
  const tooltipContent = (
    <>
      <div className={st.tooltipTitle}>{title}</div>
      <span className={st.tooltipContent}>
        <DecodedText string={content} />
      </span>
    </>
  )

  return (
    <Tooltip
      content={tooltipContent}
      position="top"
      size="m"
      tabIndex={0}
      id={id}
    >
      <span className={st.tooltipTrigger}>{title}</span>
    </Tooltip>
  )
}


type MetaDataProps = {
  detail: DetailFragment
  subtitlesLabel: string
  audioTracksLabel: string
  uniqueId: string
}

const conditionalDiv = (bool: any, content:React.ReactNode) => Boolean(bool) && <div className={st.badge}>{content}</div>

export const MetaDataTags = ({detail, subtitlesLabel, audioTracksLabel, uniqueId}: MetaDataProps) => {

  const {
    releaseYear,
    runtime,
    ratingBadge,
    subtitles,
    audioTracks,
    isXRay,
    isHdr,
    isUhd,
  } = detail!

  return (
    <Spacer className={st.tags} space={spacerSpacing.s} alignItems={spacerAlignItems.center}>
      {conditionalDiv(releaseYear, releaseYear)}
      {conditionalDiv(runtime, runtime)}
      {conditionalDiv(ratingBadge, <Rating {...{ ratingBadge }} />)}
      {conditionalDiv(safeBoolean(isXRay), <XRay {...{ isXRay }} />)}
      {conditionalDiv(safeBoolean(isHdr), <Hdr {...{ isHdr }} />)}
      {conditionalDiv(safeBoolean(isUhd), <Uhd {...{ isUhd }} />)}
      {conditionalDiv(subtitles.length, buildTooltip(subtitlesLabel, subtitles.join(', '), `subt-${uniqueId}`))}
      {conditionalDiv(audioTracks.length, buildTooltip(audioTracksLabel, audioTracks.join(', '), `at-${uniqueId}`))}
    </Spacer>
  )
}
MetaDataTags.displayName = 'MetaDataTags'

export const Packshot = ({ packshotUrl, isWatched, useResponsiveImage = true }: IPackshot) => {
  if (!packshotUrl) {
    return (
      <div className={`${st.packshot} ${st.fallbackPackshot}`}>
        <Icon use={IconType.SMILE} />
      </div>
    )
  }

  const imgProps = {
    alt: '',
    src: packshotUrl,
    baseValue: PACKSHOT_WIDTH_DESKTOP,
    srcSizes: [
      { 'max-phablet': PACKSHOT_WIDTH_MOBILE },
      { 'min-desktop': PACKSHOT_WIDTH_DESKTOP },
    ] as SrcSize[],
  }

  const classNames = cn(st.packshot, { [st.packshotDimmed]: isWatched })
  return (
    <div className={classNames}>
      {useResponsiveImage ? <ResponsiveImage {...imgProps} /> : <img src={packshotUrl} />}
    </div>
  )
}
Packshot.displayName = 'Packshot'


export const TitleListItem = (props: Props) => {
  const {
    uniqueId,
    synopsis,
    keepOpenOnSmallScreen = false,
    showDetailListButton = false,
    packshotProps,
    titleComp,
    metaDataComp,
    detailListButtonProps,
    additionalContent,
  } = props

  const epWrapperClasses = cn('js-node-episode-container', st.episode, {
    [st.withDetailListButton]: showDetailListButton,
  })

  return (
    <li className={epWrapperClasses}>
      <input
        id={`selector-${uniqueId}`}
        name="highlight-list-selector"
        className={st.expanderToggleInput}
        type="checkbox"
        defaultChecked={keepOpenOnSmallScreen}
      />
      <label className={st.expanderToggle} htmlFor={`selector-${uniqueId}`} />
      <label className={st.expanderToggleChevron} htmlFor={`selector-${uniqueId}`}>
        {/* also used in tvodButton scrollTopIntoView */}
        <Icon use={IconType.BREADCRUMB_CHEVRON} className={st.chevron} />
      </label>
      {showDetailListButton && <a {...detailListButtonProps} className={st.detailListButton} />}
      <div data-automation-id={`ep-title-${props.listIndex}`} className={st.episodeTitle}>
        {titleComp}
      </div>
      {metaDataComp && <div className={`${st.metaData} ${st.expandable}`}>{metaDataComp}</div>}
      <div className={st.expandable}>
        <Packshot {...packshotProps} />
      </div>
      {synopsis && <div className={`${st.synopsis} ${st.expandable}`}>
          <Synopsis synopsis={synopsis} numOfLines={2} expanderId={`synopsis-${uniqueId}`} />
      </div>}
      {additionalContent && <div className={`${st.expandable} ${st.additionalContent}`}>{additionalContent}</div>}
    </li>
  )
}
